import { useOrderStore } from '@/suppliers/stores/order';
import { httpClient } from '@/common/api/http-client.ts';
import { computed, type ComputedRef } from 'vue';
import { PurchaseOrderDto, PurchaseOrderListItemDto, PurchaseOrderStatus } from '@containex/portal-backend-dto';
import { purchaseOrderApi } from '@containex/portal-backend-api-client';
import { shareLink } from '../util/share-link';
import { useI18n } from 'vue-i18n';
import { immediatelyDownloadFile } from '@/util/download';
import useGlobalToast from '@/composables/useGlobalToast';

export interface OrderAction {
    fetchOrders(): Promise<void>;
    fetchOrder(orderId: string): Promise<PurchaseOrderDto>;
    shareOrderLink(): Promise<void>;
    shareProcessInstanceLink(processInstanceId: string): Promise<void>;
    downloadDocument(orderId: string): Promise<void>;
    showOrderCompletedDialog(): void;
    hideOrderCompletedDialog(): void;
}

export interface OrderQuery {
    orders: ComputedRef<PurchaseOrderListItemDto[]>;
    order: ComputedRef<PurchaseOrderDto | null>;
    orderCompletedDialogIsVisible: ComputedRef<boolean>;
}

export function useOrderQuery(): OrderQuery {
    const store = useOrderStore();

    return {
        orders: computed(() => store.orders),
        order: computed(() => store.order),
        orderCompletedDialogIsVisible: computed(() => store.orderCompletedDialogIsVisible),
    };
}

export function useOrderAction(): OrderAction {
    const store = useOrderStore();
    const { t } = useI18n();

    return {
        async fetchOrders(): Promise<void> {
            const { data } = await purchaseOrderApi.getOrders(httpClient);
            store.setOrders(data);
        },
        async fetchOrder(orderId: string): Promise<PurchaseOrderDto> {
            const previousOrder = {
                orderId: store.order?.id,
                state: store.order?.status,
            };

            store.setOrder(null);
            const { data } = await purchaseOrderApi.getOrder(httpClient, orderId);
            store.setOrder(data);

            if (
                previousOrder.orderId === orderId &&
                previousOrder.state !== data.status &&
                data.status === PurchaseOrderStatus.COMPLETED
            ) {
                store.setOrderCompletedDialogIsVisible(true);
            }

            return data;
        },
        async shareOrderLink(): Promise<void> {
            if (store.order == null) {
                return;
            }

            const data = {
                title: t('SUPPLIERS.SHARE_ORDER.TITLE', { id: store.order.id }),
                text: t('SUPPLIERS.SHARE_ORDER.DESCRIPTION', { id: store.order.id }),
                url: `${window.location.origin}${window.location.pathname}/details/${store.order.id}`,
                info: {
                    summary: 'SUPPLIERS.SHARE_ORDER.ERROR_SHARING.TITLE',
                    detail: 'SUPPLIERS.SHARE_ORDER.ERROR_SHARING.DESCRIPTION',
                },
            };

            await shareLink(data);
        },
        async shareProcessInstanceLink(processInstanceId: string): Promise<void> {
            if (store.order == null) {
                return;
            }

            const data = {
                title: t('SUPPLIERS.SHARE_PROCESS_INSTANCE.TITLE', { orderId: store.order.id }),
                text: t('SUPPLIERS.SHARE_PROCESS_INSTANCE.DESCRIPTION', {
                    orderId: store.order.id,
                }),
                url: `${window.location.origin}${window.location.pathname}/details/${store.order.id}/process-instance/${processInstanceId}`,
                info: {
                    summary: 'SUPPLIERS.SHARE_PROCESS_INSTANCE.ERROR_SHARING.TITLE',
                    detail: 'SUPPLIERS.SHARE_PROCESS_INSTANCE.ERROR_SHARING.DESCRIPTION',
                },
            };

            await shareLink(data);
        },
        async downloadDocument(orderId: string): Promise<void> {
            try {
                const response = await purchaseOrderApi.getDocument(httpClient, orderId);
                const url = response.data.url;

                if (url == null) {
                    throw Error('Document URL is null');
                }

                await immediatelyDownloadFile(url, `${orderId}.pdf`);
            } catch {
                const { errorToastOptions, addToast } = useGlobalToast();
                addToast({
                    ...errorToastOptions,
                    summary: 'ERROR.DOWNLOAD_DOCUMENT.SUMMARY',
                    detail: 'ERROR.DOWNLOAD_DOCUMENT.DETAIL',
                });
            }
        },
        showOrderCompletedDialog(): void {
            store.setOrderCompletedDialogIsVisible(true);
        },
        hideOrderCompletedDialog(): void {
            store.setOrderCompletedDialogIsVisible(false);
        },
    };
}
